import { useMutation } from "@apollo/client/react"
import { Button, CircularProgress, TextField, MenuItem } from "@mui/material"
import Alert from "@mui/material/Alert"
import { Formik } from "formik"
import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import * as yup from "yup"

import { useVenue } from "@/contexts/venue"
import { CreateVenueDocument, type CreateVenueMutation, type CreateVenueMutationVariables } from "@/graphql/graphql"
import i18n from "@/i18n"
import { usePanelLink } from "@/shared/hooks"
import { showErrorToast } from "@/shared/toast"

const STYLES = {
  container: "flex flex-col gap-6 max-w-4xl",
  header: "text-3xl font-bold mb-0",
} as const

interface FormValues {
  name: string
  country: string
  timezone: string
}

const TIMEZONES = [
  { value: "Europe/Warsaw", label: "Europe/Warsaw" },
  { value: "Europe/London", label: "Europe/London" },
  { value: "Europe/Paris", label: "Europe/Paris" },
  { value: "Europe/Berlin", label: "Europe/Berlin" },
  { value: "Europe/Rome", label: "Europe/Rome" },
  { value: "Europe/Madrid", label: "Europe/Madrid" },
]

const COUNTRIES = [
  { value: "PL", label: "Poland" },
  { value: "GB", label: "United Kingdom" },
  { value: "FR", label: "France" },
  { value: "DE", label: "Germany" },
  { value: "IT", label: "Italy" },
  { value: "ES", label: "Spain" },
]

const validationSchema = yup.object({
  name: yup.string().required(i18n.t("app.settings.venues.add_venue.errors.name_required")),
  country: yup.string().required(i18n.t("app.settings.venues.add_venue.errors.country_required")),
  timezone: yup.string().required(i18n.t("app.settings.venues.add_venue.errors.timezone_required")),
})

const AddVenue: React.FC = () => {
  const { venue } = useVenue()
  const navigate = useNavigate()
  const linkTo = usePanelLink()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [createVenue, { loading: saving }] = useMutation<CreateVenueMutation, CreateVenueMutationVariables>(
    CreateVenueDocument,
  )

  useEffect(() => {
    if (!venue.permissions?.createVenues) {
      navigate(linkTo("settings"))
    }
  }, [venue.permissions?.createVenues, navigate, linkTo])

  const initialValues: FormValues = {
    name: "",
    country: "PL",
    timezone: "Europe/Warsaw",
  }

  const handleSubmit = async (values: FormValues) => {
    setErrorMessage(null)

    try {
      const result = await createVenue({
        variables: {
          name: values.name,
          country: values.country,
          timezone: values.timezone,
        },
      })

      if (result.data?.createVenue?.success && result.data.createVenue.venue) {
        const venueSlug = result.data.createVenue.venue.slug
        navigate(`/panel/${venueSlug}/settings/venues/${venueSlug}/tabbio`)
      } else {
        const errorMessages = result.data?.createVenue?.errors?.map((e) => e.message).join(", ")
        setErrorMessage(errorMessages || i18n.t("app.settings.venues.add_venue.errors.generic"))
      }
    } catch (err) {
      showErrorToast()
      console.error("Error creating venue:", err)
      setErrorMessage(i18n.t("app.settings.venues.add_venue.errors.generic"))
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize={true}
    >
      {(formik) => (
        <form onSubmit={formik.handleSubmit} className={STYLES.container} data-testid="settings-venue-add-form">
          <h1 className={STYLES.header} data-testid="settings-venue-add-title">
            {i18n.t("app.settings.venues.add_venue.title")}
          </h1>

          {!!errorMessage && (
            <Alert severity="error" onClose={() => setErrorMessage(null)} data-testid="settings-venue-add-alert-error">
              {errorMessage}
            </Alert>
          )}

          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="space-y-4">
              <TextField
                fullWidth
                label={i18n.t("app.settings.venues.add_venue.name")}
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={!!formik.touched.name && Boolean(formik.errors.name)}
                helperText={!!formik.touched.name && formik.errors.name}
                variant="outlined"
                required
                data-testid="settings-venue-add-input-name"
              />

              <div className="grid grid-cols-2 gap-4">
                <TextField
                  fullWidth
                  select
                  label={i18n.t("app.settings.venues.add_venue.country")}
                  name="country"
                  value={formik.values.country}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={!!formik.touched.country && Boolean(formik.errors.country)}
                  helperText={!!formik.touched.country && formik.errors.country}
                  variant="outlined"
                  required
                  data-testid="settings-venue-add-select-country"
                >
                  {COUNTRIES.map((country) => (
                    <MenuItem key={country.value} value={country.value}>
                      {country.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  fullWidth
                  select
                  label={i18n.t("app.settings.venues.add_venue.timezone")}
                  name="timezone"
                  value={formik.values.timezone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={!!formik.touched.timezone && Boolean(formik.errors.timezone)}
                  helperText={!!formik.touched.timezone && formik.errors.timezone}
                  variant="outlined"
                  required
                  data-testid="settings-venue-add-select-timezone"
                >
                  {TIMEZONES.map((tz) => (
                    <MenuItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </MenuItem>
                  ))}
                </TextField>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="outlined"
              color="primary"
              onClick={() => navigate("/panel")}
              disabled={saving}
              data-testid="settings-venue-add-btn-cancel"
            >
              {i18n.t("app.settings.venues.add_venue.cancel")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={saving}
              data-testid="settings-venue-add-btn-submit"
            >
              {saving ? <CircularProgress size={24} /> : i18n.t("app.settings.venues.add_venue.create")}
            </Button>
          </div>
        </form>
      )}
    </Formik>
  )
}

export default AddVenue
